import { useEffect, useState, useContext } from "react";
import { Table, Button, Spinner, Popover, OverlayTrigger, Form } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import { IResultPayload } from "../dto/IResultPayload";
import { BaseService } from "../services/base-service";
import { IRouteSearchType } from "../types/IRouteSearchType";
import { AppContext } from "../context/AppContext";
import DownloadLink from "react-download-link";
import { BaseUrl } from "../configuration";
import { IGeodeticLocation } from "../dto/IGeodeticLocation";
import FilterIkoon from "../images/filter.png";
import {useTranslation, Trans} from 'react-i18next';

const MultiPointDisplay = (props: any) => {
    
    type FilterDropdowns = {
        [key: string]: Map<string, string[]>;
      };

    type FilterConfig = Map<string, string[]>;

    const appState = useContext(AppContext);

    const { searchType } = useParams() as IRouteSearchType;
    const queryString = useQuery().get('searchString');
    const queryExactMatch = useQuery().get('exactMatch');
    const queryHnr = useQuery().get('hnr');
    const querySnr = useQuery().get('snr');
    const queryLbn = useQuery().get('lbn');
    const queryXCoord = useQuery().get('xCoord');
    const queryYCoord = useQuery().get('yCoord');
    const queryRadius = useQuery().get('radius');
    const querySettlementName = useQuery().get('findSettlement');
    const queryPageNo = useQuery().get('pageNo');
    //const queryResultsPerPage = useQuery().get('resultsPerPage');
    let queryResultsPerPage = localStorage.getItem("numberOfResults");
    const [fetchResult, setFetchResult] = useState({} as IResultPayload);
    const [pageErrorMessage, setPageErrorMessage] = useState("");
    const [isLoading, setLoading] = useState(false);
    const [filteredLocations, setFilteredLocations] = useState([] as IGeodeticLocation[]);
    const [activeFilter, setActiveFilter] = useState(new Map<string, Map<string, string[]>>());
    const {t} = useTranslation();

    const filterMetadata: FilterConfig = new Map([
        ['network', ['klassXy', 'klassKorgus', 'klassGr']],
        ['condition', ['skoodTxt']],
    ]);

    const initializeFilterDropdowns = (): FilterDropdowns => {

        const filterKeys = Array.from(filterMetadata.keys());

        const initialFilterDropdowns: FilterDropdowns = {};
            filterKeys.forEach((key) => {
            initialFilterDropdowns[key] = new Map<string, string[]>();
        });
        return initialFilterDropdowns;
      };

    const [filterDropdowns, setFilterDropdowns] = useState<FilterDropdowns>(initializeFilterDropdowns());

    const fetchData = async (pageNo: string, resultsPerPage: string) => {
        setPageErrorMessage("");
        setLoading(true);
        let result;
        let urlQueryString = "";
        let url = "";
        let loadAll = false;
        if (pageNo === "all") {
            loadAll = true;
            pageNo = "0";
            resultsPerPage = fetchResult.totalElements.toString();
        }
        switch (searchType) {
          case "name":
            urlQueryString = `?searchString=${queryString}&exactMatch=${queryExactMatch}&pageNo=${pageNo}`;
            result = await searchByName(pageNo, resultsPerPage);
            url = `/multiplelocations/name/${urlQueryString}`;
            break;
          case "settlement":
            urlQueryString = `?findSettlement=${querySettlementName}&pageNo=${pageNo}`;
            result = await searchBySettlement(pageNo, resultsPerPage);
            url = `/multiplelocations/settlement/${urlQueryString}`;
            break;
          case "code":
            urlQueryString = `?hnr=${queryHnr}&snr=${querySnr}&lbn=${queryLbn}&pageNo=${pageNo}`;
            result = await searchByCode(pageNo, resultsPerPage);
            url = `/multiplelocations/code/${urlQueryString}`;
            break;
          case "coords":
            urlQueryString = `?xCoord=${queryXCoord}&yCoord=${queryYCoord}&radius=${queryRadius}&pageNo=${pageNo}`;
            result = await searchByCoords(pageNo, resultsPerPage);
            url = `/multiplelocations/coords/${urlQueryString}`;
            break;
          default:
            break;
        }
        if (pageNo !== "0") {
            window.history.replaceState(null, "", url);
        }
        if (result === undefined) {
          return;
        }
        if (result.ok && result.data) {
          let payload = result.data as IResultPayload;
          let data = fetchResult.allLocations || [];
          if (payload.allLocations !== undefined) {
            if (!loadAll) {
                data = data?.concat(payload.allLocations!);
                payload.allLocations = data;
            }
          }
          setLoading(false);
          setFetchResult(payload);
          createFilterDropdownContent(result.data!.allLocations!);
          //createFilterDropdownContent(result.data!.allLocations!);
          setFilteredLocations(filterLocations(payload.allLocations!, activeFilter));
        } else {
          setPageErrorMessage(t('requestError').toString());
          setLoading(false);
          //setPageStatus({ pageStatus: EPageStatus.Error, statusCode: result.statusCode });
        }
      };
      
      const loadData = async () => {
        setPageErrorMessage("");
        let pageNo = queryPageNo ? queryPageNo : "0";
        let newNumberOfResults = Number.parseInt(queryResultsPerPage ? queryResultsPerPage : "25") * (Number.parseInt(pageNo) + 1);

        await fetchData("0", newNumberOfResults.toString());
        setTimeout(() => {
          const scrollPosition = localStorage.getItem("scrollPosition");
          if (scrollPosition) {
            window.scrollTo(0, Number.parseInt(scrollPosition));
            localStorage.removeItem("scrollPosition");
          }
        }, 200);
      };
      
    const loadMoreData = async (e: Event, pageNo: string, resultsPerPage: string) => {
        e.preventDefault();
        await fetchData(pageNo, resultsPerPage);
      };

    const createFilterDropdownContent = (locations: IGeodeticLocation[]) => {
        let updatedFilterDropdowns: FilterDropdowns = { ...filterDropdowns };
    
        filterMetadata.forEach((fields, type) => {
            let tempFilters = updatedFilterDropdowns[type] || new Map<string, string[]>([]);
    
            if (locations === undefined) {
                return;
            }
    
            locations.forEach((current) => {
                fields.forEach((field) => {
                    const fieldValue = (current as any)[field]?.trim();
                    if (!tempFilters.has(field)) {
                        tempFilters.set(field, []);
                    }
                    if (fieldValue && !tempFilters.get(field)?.includes(fieldValue)) {
                        tempFilters.get(field)?.push(fieldValue);
                    }
                });
            });
            updatedFilterDropdowns = {
                ...updatedFilterDropdowns,
                [type]: tempFilters,
            };
        });
        setFilterDropdowns(updatedFilterDropdowns);
    }

    const filterLocations = (locations: IGeodeticLocation[], filter: Map<string, Map<string, string[]>>) => {
        let finalResult: IGeodeticLocation[] = [];
    
        const typeFilterKeys = Array.from(filter.keys());
    
        if (typeFilterKeys.every(type => {
            const typeFilter = filter.get(type) || new Map<string, string[]>();
            return Array.from(typeFilter.values()).every(fieldValues => fieldValues.length === 0);
        }) || locations === undefined) {
            return locations;
        }
    
        locations.forEach(current => {
            const matches = typeFilterKeys.every(type => {
                const typeFilter = filter.get(type) || new Map<string, string[]>();
                return Array.from(typeFilter.entries()).every(([field, filterValues]) => {
                    const actualFieldValue = (current as any)[field]?.trim();
                    return (
                        filterValues.length === 0 ||
                        (actualFieldValue !== undefined && actualFieldValue !== null && filterValues.includes(actualFieldValue))
                    );
                });
            });
    
            if (matches) {
                finalResult.push(current);
            }
        });
    
        return finalResult;
    }
    
    

    const addNewFilter = (type: string, field: string, newFilter: string) => {
        const typeFilter = activeFilter.get(type) || new Map<string, string[]>();

        if (typeFilter) {
          const selectedValues = typeFilter.get(field) || [];
      
          const isFilterSelected = selectedValues.includes(newFilter);
      
          if (isFilterSelected) {
            const updatedValues = selectedValues.filter(value => value !== newFilter);
            typeFilter.set(field, updatedValues);
          } else {
            selectedValues.push(newFilter);
            typeFilter.set(field, selectedValues);
          }
      
          setActiveFilter(new Map(activeFilter).set(type, typeFilter));
          setFilteredLocations(filterLocations(fetchResult.allLocations!, activeFilter));
        }
      };
      
    
    function useQuery() {
        return new URLSearchParams(window.location.search);
    }


    const newNumberOfResults = (newNumber: string) => {
        appState.setNumberOfResults(newNumber);
        queryResultsPerPage = newNumber;
        localStorage.setItem("numberOfResults", newNumber);
        loadData(); 
    }
    
    const searchByName = async (pageNo: string, resultsPerPage: string) => {
        const searchString = queryString ? queryString : "";
        const exactMatch = queryExactMatch ? queryExactMatch : "";
        return await BaseService.findByName<IResultPayload>("/findbyname", searchString, exactMatch, pageNo, resultsPerPage);

    };

    const searchBySettlement = async (pageNo: string, resultsPerPage: string) => {
        const searchedSettlement = querySettlementName ? querySettlementName : "";
        return await BaseService.findBySettlement<IResultPayload>("/findbysettlement", searchedSettlement, pageNo, resultsPerPage);

    };

    const searchByCode = async (pageNo: string, resultsPerPage: string) => {
        const hnr = queryHnr ? queryHnr : "";
        const snr = querySnr ? querySnr : "";
        const lbn = queryLbn ? queryLbn : "";
        return await BaseService.findByCode<IResultPayload>("/findbycode", hnr, snr, lbn, pageNo, resultsPerPage);
    };

    const searchByCoords = async (pageNo: string, resultsPerPage: string) => {
        const xCoord = queryXCoord ? queryXCoord : "";
        const yCoord = queryYCoord ? queryYCoord : "";
        const radius = queryRadius ? queryRadius : "";
        return await BaseService.findByCoords<IResultPayload>("/findbycoords", xCoord, yCoord, radius, pageNo, resultsPerPage);
    };

    const saveToStorage = () => {
        localStorage.setItem('scrollPosition', window.pageYOffset.toString());
    
        filterMetadata.forEach((fields, type) => {
            const filterValues = activeFilter.get(type) || new Map<string, string[]>();
            localStorage.setItem(`${type}Filter`, JSON.stringify(Array.from(filterValues.entries())));
        });
    };
        
    const restoreFilterFromStorage = () => {
        const updatedFilter = new Map<string, Map<string, string[]>>();
    
        filterMetadata.forEach((fields, type) => {
            const filterValuesKey = `${type}Filter`;
            const filterValues = localStorage.getItem(filterValuesKey);
            if (filterValues) {
                const parsedFilterValues = JSON.parse(filterValues);
                const filterValuesMap = new Map<string, string[]>(parsedFilterValues);
                updatedFilter.set(type, filterValuesMap);
            } else {
                updatedFilter.set(type, new Map<string, string[]>());
            }
        });

        setActiveFilter(updatedFilter);
    };

    useEffect(() => {

        if (localStorage.getItem("numberOfResults") !== null) {
            appState.setNumberOfResults(localStorage.getItem("numberOfResults"));
        }
        restoreFilterFromStorage();
       
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (fetchResult.allLocations === undefined) {
            loadData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeFilter]);

    const generateCSVFileResult = async() => {

        let result;
        if (searchType === "name") {
            result = await searchByName("0", fetchResult.totalElements.toString());
        }

        if (searchType === "settlement") {
            result = await searchBySettlement("0", fetchResult.totalElements.toString());
        }

        if (searchType === "code") {
            result = await searchByCode("0", fetchResult.totalElements.toString());
        }
        
        if (searchType === "coords") {
            result = await searchByCoords("0", fetchResult.totalElements.toString());
        }

        if (result === undefined) {
            return;
        }
    
        let resultCSV = t('resultId') + ";URL; " + t('resultName') + ";" + t('resultCode') + ";x L-EST97;y L-EST97;H EH2000;h EUREF-97;g EG2000;" + t('resultNetwork') + ";" + t('resultSettlemUnit') + ";" + t('resultCondition') + ";" + t('resultComments') + "\r\n";
        const filteredResults = filterLocations(result.data?.allLocations!, activeFilter);
        filteredResults.forEach (location => 
            {
                resultCSV += location.refNrId + ";";
                resultCSV += BaseUrl + "/geolocation/" + location.refNrId + ";";
                resultCSV += location.nimi + ";";
                resultCSV += location.hnr + "-" + location.snr + "-" + location.lbn + ";";
                resultCSV += location.xyKoht ? location.x?.toFixed(location.xyKoht) + ";" : location.x + ";";
                resultCSV += location.xyKoht ? location.y?.toFixed(location.xyKoht) + ";" : location.y + ";";
                if (location.eh2000) {
                    resultCSV += location.korgusKoht ? location.eh2000?.toFixed(location.korgusKoht) + ";" : location.eh2000 + ";";
                } else {
                    resultCSV += "-;"
                }
                if (location.elk97) {
                    resultCSV += location.elk97Koht ? location.elk97?.toFixed(location.elk97Koht) + ";": location.elk97 + ";";
                } else {
                    resultCSV += "-;"
                }
                resultCSV += location.rkRkiirendus ? location.rkRkiirendus + ";" : "-;";
                resultCSV += (location.klassXy ? location.klassXy : "" + location.klassKorgus ? location.klassKorgus : " " + location.klassGr ? location.klassGr : " ") + ";";
                resultCSV += location.asustusyksus ? location.asustusyksus + ";" : "-;";
                resultCSV += location.skoodTxt ? location.skoodTxt + ";": "-;";
                resultCSV += location.smarkus ? location.smarkus : "-";
                resultCSV += "\r\n";
            }
        );
        return resultCSV;
    }

    const createFilterDropdownMenu = (type: string, dropdownData: Map<string, string[]>) => (
        <Popover id="popover-contained" style={{ width: "100%" }}>
            <Popover.Body style={{ maxHeight: "300px", overflowY: "auto" }}>
                {dropdownData !== undefined && (
                    <>
                        <Form>
                            {Array.from(dropdownData.entries()).map(([field, values]) => (
                                values.length > 0 && (
                                    <div key={field}>
                                        <div className="fw-bold">{t(field)}</div>
                                        {values.map((currentFilter, index) => (
                                            <Form.Check
                                                key={`${field}-${currentFilter}`}
                                                type="checkbox"
                                                id={`checkbox-${field}-${index}`}
                                                label={currentFilter}
                                                checked={activeFilter.get(type)?.get(field)?.includes(currentFilter)}
                                                onChange={() => addNewFilter(type, field, currentFilter)}
                                            />
                                        ))}
                                    </div>
                                )
                            ))}
                        </Form>
                    </>
                )}
            </Popover.Body>
        </Popover>
    );
        

    return (
        <>
            {Object.keys(fetchResult).length > 0 ?
                <Table striped bordered size="sm">
                    <thead style={{position: "sticky", top: "-1px", backgroundColor: "white"}}>
                        <tr>
                            <th style={{ width: "60px" }}>
                                {t('resultId')}
                            </th>
                            <th style={{ width: "60px" }}>
                                {t('resultPointNumber')}
                            </th>
                            <th style={{ width: "150px" }}>
                                {t('resultName')}
                            </th>
                            <th style={{ width: "120px" }} className="cell-center">
                                {t('resultCode')}
                            </th>
                            <th colSpan={2} style={{ width: "200px" }}>
                                <table>
                                    <tbody>
                                        <tr>
                                            <td colSpan={2} className="cell-center">{t('resultPlaneTrCoords')}</td>
                                        </tr>
                                        <tr>
                                            <td className="cell-center">
                                                x<sub>L-EST97</sub>
                                            </td>
                                            <td className="cell-center">
                                                y<sub>L-EST97</sub>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </th>
                            <th style={{ width: "60px" }} className="cell-center">
                                {t('resultNormHeight')}<sub>EH2000</sub>
                            </th>
                            <th style={{ width: "60px" }} className="cell-center">
                                {t('resultEllipsoidHeight')} <sub>EUREF-EST97</sub>
                            </th>
                            <th style={{ width: "60px" }} className="cell-center">
                                {t('resultGrAcceleration')}<sub>EG2000</sub>
                            </th>
                            <th style={{ width: "170px" }}>
                                <table>
                                    <tbody>
                                        <tr>
                                            <td className="cell-center" style={{ padding: 0, margin: 0 }}>
                                                {t('resultNetwork')}
                                            </td>
                                            <td className="cell-center" style={{ width: "12px", padding: 0, margin: 0 }}>
                                                <OverlayTrigger
                                                    trigger="click"
                                                    placement="bottom"
                                                    rootClose
                                                    overlay={createFilterDropdownMenu("network", filterDropdowns["network"])}
                                                >
                                                    <img className="filter-icon" src={FilterIkoon} alt="Filter"/>
                                                </OverlayTrigger>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </th>
                            <th style={{ width: "200px" }}>
                                {t('resultSettlemUnit')}
                            </th>
                            <th style={{ width: "100px"}}>
                                <table>
                                    <tbody>
                                        <tr>
                                            <td className="cell-center" style={{ padding: 0, margin: 0 }}>
                                                {t('resultCondition')}
                                            </td>
                                            <td className="cell-center" style={{ width: "12px", padding: 0, margin: 0 }}>
                                                <OverlayTrigger 
                                                    trigger="click" 
                                                    placement="bottom" 
                                                    rootClose
                                                    overlay={createFilterDropdownMenu("condition", filterDropdowns["condition"])}
                                                >
                                                    <img className="filter-icon" src={FilterIkoon} alt="Filter"/>
                                                </OverlayTrigger>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </th>
                            <th style={{ width: "100px"}}>
                                {t('resultComments')}
                            </th>
                        </tr>
                    </thead>

                    <tbody>
                        <tr>
                            <td colSpan={13}>
                                <div className="d-flex justify-content-between">
                                    <div>
                                        {
                                            (searchType === "name") &&
                                            <span>
                                                {t('resultSearchByName')}: <b>{queryString}</b>.{" "}
                                            </span>
                                        }
                                        {
                                            (searchType === "code") &&
                                                <span>
                                                    {t('resultSearchByCode')}: <b>{queryHnr !== "" ? queryHnr: "x"}-{querySnr !== "" ? querySnr: "x"}-{queryLbn !== "" ? queryLbn: "x"}</b>.{" "}
                                                </span>
                                        }
                                        {
                                            (searchType === "coords") &&
                                                <span>
                                                    <Trans i18nKey="resultSearchByCoords" values={{xCoord: queryXCoord, yCoord: queryYCoord, radius: queryRadius, escapeInterpolation: true}} components={[<b />]}/>
                                                </span>
                                        }
                                        {
                                            (searchType === "settlement") &&
                                                <span>
                                                    {t('resultSearchBySettlementUnit')}:{" "}<b>{querySettlementName}</b>.
                                                </span>
                                        }
                                        {t('results')}: <b>{fetchResult.allLocations?.length + " (" + fetchResult.totalElements + ")"}</b>
                                        {
                                            (fetchResult.totalElements > fetchResult.allLocations?.length!) &&

                                                <span>(<Link className="link" to={"#"} onClick={(e) => {e.preventDefault(); loadMoreData(e.nativeEvent, "all", fetchResult.totalElements.toString())}}>{t('showAllResults')}</Link>)</span>
                                        }
                                        </div>
                                    <div>
                                        {t('resultsLoadingAtOnce')}: {" "}
                                        {appState.numberOfResults !== "10" ? <Link className="link" to={"#"} onClick={(e) => {e.preventDefault(); newNumberOfResults("10")}}>10</Link> : 10 } {" | "}
                                        {appState.numberOfResults !== "25" ? <Link className="link" to={"#"} onClick={(e) => {e.preventDefault(); newNumberOfResults("25")}}>25</Link> : 25 } {" | "}
                                        {appState.numberOfResults !== "50" ? <Link className="link" to={"#"} onClick={(e) => {e.preventDefault(); newNumberOfResults("50")}}>50</Link> : 50 } {" | "}
                                        {appState.numberOfResults !== "100" ? <Link className="link" to={"#"} onClick={(e) => {e.preventDefault(); newNumberOfResults("100")}}>100</Link> : 100 } {" | "}
                                        {appState.numberOfResults !== "250" ? <Link className="link" to={"#"} onClick={(e) => {e.preventDefault(); newNumberOfResults("250")}}>250</Link> : 250 } {" | "}
                                        {appState.numberOfResults !== "500" ? <Link className="link" to={"#"} onClick={(e) => {e.preventDefault(); newNumberOfResults("500")}}>500</Link> : 500 }
                                    </div>
                                </div>
                            </td>
                        </tr>
                        {
                            filteredLocations.length !== 0 &&
                            filteredLocations.map (location => (
                                <>
                                    <tr key={location.refNrId}>
                                        <td><Link className="link" onClick={() => saveToStorage()} to={"/geolocation/" + location.refNrId}>{location.refNrId}</Link></td>
                                        <td>{location.lbn}</td>
                                        <td>{location.nimi}</td>
                                        <td className="cell-center">{location.hnr + "-" + location.snr + "-" + location.lbn}</td>
                                        <td className="cell-center">{location.xyKoht ? location.x?.toFixed(location.xyKoht): location.x}</td>
                                        <td className="cell-center">{location.xyKoht ? location.y?.toFixed(location.xyKoht): location.y}</td>
                                        <td className="cell-center">{location.eh2000 ? parseFloat(location.eh2000?.toString()).toFixed(location.korgusKoht ? location.korgusKoht : 4) : ""}</td>
                                        <td className="cell-center">{location.elk97 ? parseFloat(location.elk97?.toString()).toFixed(location.elk97Koht ? location.elk97Koht : 3) : ""}</td>
                                        <td className="cell-center">{location.rkRkiirendus ? location.rkRkiirendus : ""}</td>
                                        <td>
                                            {location.klassXy &&
                                                <>x, y: {location.klassXy}.<br /></>
                                            }
                                            {location.klassKorgus && 
                                                <>H: {location.klassKorgus}.<br /></>
                                            }
                                            {location.klassGr && 
                                                <>g: {location.klassGr}.<br /></>
                                            }
                                        </td>
                                        <td>{location.asustusyksus}</td>
                                        <td>{location.skoodTxt}</td>
                                        <td>{location.smarkus}</td>
                                    </tr>

                                </>
                            ))
                        }
                        {(fetchResult.allLocations !== undefined && fetchResult.allLocations?.length > 0) &&
                            <tr>
                                <td colSpan={13}>
                                    <div className="d-flex justify-content-center">
                                        <DownloadLink 
                                            style={{display: "block",
                                                padding: "0.5rem 1rem",
                                                textDecoration: "none",
                                                transition: "color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out",
                                                color: "#707070"
                                            }} 
                                            label={t('downloadSearchResult').toString()} 
                                            filename={"Otsing.csv"} 
                                            exportFile={() => generateCSVFileResult()} 
                                        />
                                    </div>
                                </td>
                            </tr>
                        }
                        {(fetchResult.pageNo + 1) < fetchResult.totalPages &&
                            <>
                                <tr>
                                    <td colSpan={13}>
                                        <div className="d-grid gap-2">
                                            <Button variant="primary" onClick={(e) => loadMoreData(e.nativeEvent, ((queryPageNo ? Number.parseInt(queryPageNo) : 0) + 1).toString(), queryResultsPerPage ? queryResultsPerPage : "25")} size="lg">
                                                {isLoading ? <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> {t('resultLoadingMore')}</> : t('resultLoadMore') + " (" + (fetchResult.totalElements - (fetchResult.pageNo + 1) * fetchResult.resultsPerPage) + ")"}
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            </>
                        }
                    </tbody>
                </Table>
            : 
                (pageErrorMessage === "") ? <Spinner className="center" animation="border" /> : pageErrorMessage
            }

        </>
    );

}

export default MultiPointDisplay;

